import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { VehicleLog, VehicleLogFilters } from "./types";
import { vehicleLogApi } from "./vehicle-log-api";
import { campusApi } from "../campus/campus-api";
import { Campus } from "../campus/types";

const VehicleLogsHistory: React.FC = () => {
  const [logs, setLogs] = useState<VehicleLog[]>([]);
  const [campuses, setCampuses] = useState<Campus[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);

  // Default to last 1 week
  const getDefaultDateRange = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    return {
      from: start.toISOString().slice(0, 16),
      to: end.toISOString().slice(0, 16),
    };
  };

  const [filters, setFilters] = useState<VehicleLogFilters>({
    date_from: getDefaultDateRange().from,
    date_to: getDefaultDateRange().to,
    status: "all",
  });
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchCampuses();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [page, filters, searchQuery]);

  const fetchCampuses = async () => {
    try {
      const data = await campusApi.getAllCampuses();
      setCampuses(data);
    } catch (error) {
      console.error("Failed to load campuses");
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const filtersWithSearch: VehicleLogFilters = {
        ...filters,
        search: searchQuery || undefined,
      };
      const response = await vehicleLogApi.getVehicleLogHistory(filtersWithSearch, page, limit);
      setLogs(response.data);
      setTotal(response.total);
      setTotalPages(response.totalPages);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to load vehicle logs");
      setLogs([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: keyof VehicleLogFilters, value: any) => {
    setFilters({ ...filters, [key]: value });
    setPage(1); // Reset to first page when filters change
  };

  const clearFilters = () => {
    const defaultRange = getDefaultDateRange();
    setFilters({
      date_from: defaultRange.from,
      date_to: defaultRange.to,
      status: "all",
    });
    setSearchQuery("");
    setPage(1);
  };

  const getUniqueValues = (key: keyof VehicleLog) => {
    const values = new Set<string>();
    logs.forEach((log) => {
      const value = log[key];
      if (value && typeof value === "string") {
        values.add(value);
      }
    });
    return Array.from(values).sort();
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-8 px-4">
      <div className="max-w-[1800px] mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Vehicle Logs History
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            View historical vehicle entry and exit records
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Search
              </label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search by plate, owner, campus..."
              />
            </div>

            {/* Vehicle Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Vehicle Type
              </label>
              <input
                type="text"
                value={filters.vehicle_type || ""}
                onChange={(e) => handleFilterChange("vehicle_type", e.target.value || undefined)}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Car, Bike, Truck..."
              />
            </div>

            {/* Make */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Make
              </label>
              <input
                type="text"
                value={filters.make || ""}
                onChange={(e) => handleFilterChange("make", e.target.value || undefined)}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Toyota, Honda..."
              />
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <select
                value={filters.status || "all"}
                onChange={(e) =>
                  handleFilterChange("status", e.target.value as "entry" | "exit" | "all")
                }
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All</option>
                <option value="entry">Entry</option>
                <option value="exit">Exit</option>
              </select>
            </div>

            {/* Inside Campus */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Location
              </label>
              <select
                value={filters.is_inside === undefined ? "" : filters.is_inside ? "inside" : "outside"}
                onChange={(e) =>
                  handleFilterChange(
                    "is_inside",
                    e.target.value === "" ? undefined : e.target.value === "inside"
                  )
                }
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All</option>
                <option value="inside">Inside Campus</option>
                <option value="outside">Outside Campus</option>
              </select>
            </div>

            {/* Campus */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Campus
              </label>
              <select
                value={filters.campus_id || ""}
                onChange={(e) =>
                  handleFilterChange("campus_id", e.target.value ? Number(e.target.value) : undefined)
                }
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Campuses</option>
                {campuses.map((campus) => (
                  <option key={campus.id} value={campus.id}>
                    {campus.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Owner Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Owner Name
              </label>
              <input
                type="text"
                value={filters.owner_name || ""}
                onChange={(e) => handleFilterChange("owner_name", e.target.value || undefined)}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search owner name..."
              />
            </div>

            {/* Member Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Member Name
              </label>
              <input
                type="text"
                value={filters.member_name || ""}
                onChange={(e) => handleFilterChange("member_name", e.target.value || undefined)}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search member name..."
              />
            </div>

            {/* Date From */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Date From
              </label>
              <input
                type="datetime-local"
                value={filters.date_from || ""}
                onChange={(e) => handleFilterChange("date_from", e.target.value || undefined)}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Date To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Date To
              </label>
              <input
                type="datetime-local"
                value={filters.date_to || ""}
                onChange={(e) => handleFilterChange("date_to", e.target.value || undefined)}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            <button
              onClick={clearFilters}
              className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Clear Filters
            </button>
            <button
              onClick={fetchLogs}
              className="btn-primary px-4 py-2"
              disabled={loading}
            >
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>

        {/* Results Info */}
        <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
          Showing {logs.length} of {total} records (Page {page} of {totalPages})
        </div>

        {/* Logs Table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                      Timestamp
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                      Plate No
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                      Vehicle
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                      Campus
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                      Entry Node/Camera
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                      Exit Node/Camera
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                      Owner/Member
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                  {logs.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                        No logs found for the selected filters
                      </td>
                    </tr>
                  ) : (
                    logs.map((log) => (
                      <tr
                        key={log.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {new Date(log.timestamp||"").toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          {log.number_plate || log.reg_no}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          <div>
                            {log.vehicle_type && <div>{log.vehicle_type}</div>}
                            {log.make && <div className="text-xs">{log.make}</div>}
                            {log.color && <div className="text-xs text-gray-400">{log.color}</div>}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {log.campus ? (
                            <div>
                              <div className="font-medium">{log.campus.name}</div>
                              <div className="text-xs">{log.campus.location}</div>
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {log.entry_node ? (
                            <div>
                              <div className="font-medium">{log.entry_node.node_name}</div>
                              {log.entry_camera?.camera_id && (
                                <div className="text-xs">Cam: {log.entry_camera.camera_id}</div>
                              )}
                              {log.entry_time && (
                                <div className="text-xs text-gray-400">
                                  {new Date(log.entry_time).toLocaleTimeString()}
                                </div>
                              )}
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {log.exit_node ? (
                            <div>
                              <div className="font-medium">{log.exit_node.node_name}</div>
                              {log.exit_camera?.cam_id && (
                                <div className="text-xs">Cam: {log.exit_camera.cam_id}</div>
                              )}
                              {log.exit_time && (
                                <div className="text-xs text-gray-400">
                                  {new Date(log.exit_time).toLocaleTimeString()}
                                </div>
                              )}
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {log.owner ? (
                            <div>
                              <div className="font-medium">{log.owner.name}</div>
                              {log.owner.phone && <div className="text-xs">{log.owner.phone}</div>}
                              <div className="text-xs">
                                {log.owner.is_member ? (
                                  <span className="text-green-600">Member</span>
                                ) : (
                                  <span className="text-gray-400">Owner</span>
                                )}
                              </div>
                            </div>
                          ) : log.member ? (
                            <div>
                              <div className="font-medium">{log.member.name}</div>
                              {log.member.phone && <div className="text-xs">{log.member.phone}</div>}
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              log.status === "entry"
                                ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                                : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                            }`}
                          >
                            {log.status === "entry" ? "Entry" : "Exit"}
                          </span>
                          {log.is_inside && (
                            <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                              Inside
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-6">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <div className="flex gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum:number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    disabled={loading}
                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      page === pageNum
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || loading}
              className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VehicleLogsHistory;
